import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import FirewallModal from '../../components/Firewall/FirewallModal';
import { FaTrash } from 'react-icons/fa';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, {
      tags: { section: 'advanced-configuration' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, {
      level: 'error',
      tags: { section: 'advanced-configuration' },
      extra: context
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'advanced-configuration',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Keep in sync with backend MAX_BULK_DELETE in views.py
// Ideally import from a shared constants file:
//   import { MAX_BULK_DELETE } from '../../constants/limits';
const MAX_BULK_DELETE = 10;

// Bulk Delete Modal Component
const BulkDeleteModal = ({ isOpen, onClose, scripts, onDelete, onRefresh }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const isAtLimit = selectedIds.length >= MAX_BULK_DELETE;

  useEffect(() => {
    if (isOpen && onRefresh) {
      onRefresh();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setShowConfirmation(false);
      setError('');
    }
  }, [isOpen]);

  const handleToggleScript = (scriptId) => {
    setSelectedIds(prev => {
      if (prev.includes(scriptId)) {
        return prev.filter(id => id !== scriptId);
      }
      if (prev.length >= MAX_BULK_DELETE) {
        return prev;
      }
      return [...prev, scriptId];
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(scripts.slice(0, MAX_BULK_DELETE).map(s => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const isItemDisabled = (scriptId) => {
    return isAtLimit && !selectedIds.includes(scriptId);
  };

  const handleDelete = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setError('');
    try {
      await onDelete(selectedIds);
      setShowConfirmation(false);
      setSelectedIds([]);
      onClose();
    } catch (error) {
      setError('Failed to delete scripts. Please try again.');
      logError('Bulk delete failed', { selectedCount: selectedIds.length }, error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Bulk Delete Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121212] rounded-lg w-full max-w-3xl max-h-[85vh] border border-[#333333] flex flex-col">
          
          {/* Header */}
          <div className="p-4 border-b border-[#333333] flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-white">Bulk Delete Scripts</h3>
              <p className="text-xs text-gray-400 mt-1">
                Select up to {MAX_BULK_DELETE} scripts to delete permanently
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2"
              disabled={isDeleting}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Selection Controls */}
          <div className="p-4 border-b border-[#333333] flex justify-between items-center bg-[#1a1a1a]">
            <div className="flex gap-3">
              <button
                onClick={handleSelectAll}
                className="text-xs px-3 py-1.5 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors"
                disabled={isDeleting}
              >
                {scripts.length > MAX_BULK_DELETE ? `Select First ${MAX_BULK_DELETE}` : 'Select All'}
              </button>
              <button
                onClick={handleDeselectAll}
                className="text-xs px-3 py-1.5 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors"
                disabled={selectedIds.length === 0 || isDeleting}
              >
                Deselect All
              </button>
            </div>
            <div className="flex items-center gap-3">
              {isAtLimit && (
                <span className="text-xs text-yellow-400">
                  Limit reached
                </span>
              )}
              <span className={`text-sm ${isAtLimit ? 'text-yellow-400' : 'text-gray-300'}`}>
                {selectedIds.length}/{MAX_BULK_DELETE} selected
              </span>
            </div>
          </div>

          {/* Scripts List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar flex flex-col">
            {scripts.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No scripts available
              </div>
            ) : (
              scripts.map((script) => {
                const isSelected = selectedIds.includes(script.id);
                return (
                  <div
                    key={script.id}
                    onClick={() => !isDeleting && !isItemDisabled(script.id) && handleToggleScript(script.id)}
                    className={`
                      w-full p-3 rounded-md transition-all border min-h-[80px] flex items-center
                      ${isSelected 
                        ? 'bg-[rgba(238,85,85,0.05)] border-[rgba(255,119,119,0.3)] cursor-pointer' 
                        : 'bg-[#1a1a1a] border-[#333333] hover:border-[#444444] cursor-pointer'
                      }
                      ${isDeleting || isItemDisabled(script.id) ? 'opacity-40 cursor-not-allowed' : ''}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                          ${isSelected 
                            ? 'bg-[#ff7777] border-[#ff7777]' 
                            : 'border-[#333333] bg-[#121212]'
                          }
                        `}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* Script Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <h4 className={`font-medium text-sm ${isSelected ? 'text-[#ff7777]' : 'text-white'}`}>
                            {sanitizeText(script.name)}
                          </h4>
                        </div>
                        {script.script_preview && (
                          <p className="text-xs text-gray-400 mt-1 font-mono truncate">
                            {sanitizeText(script.script_preview)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#333333] flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={selectedIds.length === 0 || isDeleting}
              className={`
                px-4 py-2 rounded font-medium transition-all flex items-center gap-2
                ${selectedIds.length > 0 && !isDeleting
                  ? 'bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)]'
                  : 'bg-[#333333] text-gray-500 cursor-not-allowed border border-[#333333]'
                }
              `}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                  Deleting...
                </>
              ) : (
                <>
                  Delete {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#121212] rounded-lg w-full max-w-md border border-[#333333]">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Confirm Deletion
              </h3>
              <p className="text-gray-300 text-sm mb-6">
                Are you sure you want to delete <span className="font-bold text-[#ff7777]">{selectedIds.length} script{selectedIds.length > 1 ? 's' : ''}</span>? This action cannot be undone.
              </p>
              {error && (
                <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-800 rounded-md">
                  <div className="flex items-start">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-400 text-xs">{error}</p>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)] rounded font-medium transition-all"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Scripts'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

BulkDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scripts: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRefresh: PropTypes.func
};

const AdvancedConfiguration = ({
    publicIP,
    onTogglePublicIP,
    onManageFirewall,
    onAddCloudInit,
    securityRules = [],
    onUpdateSecurityRules = () => {},
    isAdvancedRegion = false,
    isAuthenticated = false,
    scripts = [],
    selectedScript = '',
    onScriptChange = () => {},
    onCreateScript = () => {},
    onEditScript = () => {},
    onDeleteScript = () => {},
    onRefreshScripts = () => {},
    loadingScripts = false,
    onBulkDeleteScripts = () => {}
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [firewallModalOpen, setFirewallModalOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(isAdvancedRegion ? 'startup' : 'network');
    const [isScriptDropdownOpen, setIsScriptDropdownOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const scriptDropdownRef = useRef(null);

    const [loadingStates, setLoadingStates] = useState({
        firewall: false,
        savingRules: false,
        scripts: false,
        deletingScript: false,
        bulkDeleting: false
    });

    useEffect(() => {
        Sentry.setContext("advanced-configuration", {
            isExpanded,
            activeSection,
            publicIP,
            hasSecurityRules: securityRules && securityRules.length > 0,
            firewallModalOpen,
            isAdvancedRegion,
            isAuthenticated,
            scriptsCount: scripts?.length || 0,
            selectedScript,
            loadingScripts,
            bulkDeleteModalOpen
        });
    }, [isExpanded, activeSection, publicIP, securityRules, firewallModalOpen, 
        isAdvancedRegion, isAuthenticated, scripts, selectedScript, loadingScripts, bulkDeleteModalOpen]);

    useEffect(() => {
        logInfo('AdvancedConfiguration component mounted', {
            publicIP,
            securityRulesCount: securityRules?.length || 0,
            isAdvancedRegion,
            isAuthenticated,
            scriptsCount: scripts?.length || 0
        });
    }, []);

    useEffect(() => {
        if (isAdvancedRegion && activeSection !== 'startup') {
            setActiveSection('startup');
            logInfo('Active section updated for advanced region', {
                previousSection: activeSection,
                newSection: 'startup'
            });
        }
    }, [isAdvancedRegion, activeSection]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (scriptDropdownRef.current && !scriptDropdownRef.current.contains(event.target)) {
                setIsScriptDropdownOpen(false);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleOperationError = (error, operation) => {
        logError(`Operation failed: ${operation}`, {
            operation,
            errorMessage: error?.message,
            activeSection,
            isExpanded,
            isAdvancedRegion,
            isAuthenticated
        }, error);
    };

    const toggleExpanded = () => {
        try {
            const newExpandedState = !isExpanded;
            setIsExpanded(newExpandedState);

            logUserAction('advanced_config_toggle', {
                expanded: newExpandedState,
                activeSection,
                isAdvancedRegion,
                isAuthenticated
            });

            logInfo('Advanced configuration section toggled', {
                expanded: newExpandedState,
                activeSection,
                isAdvancedRegion,
                isAuthenticated
            });
        } catch (error) {
            handleOperationError(error, 'general');

            logUserAction('advanced_config_toggle_error', {
                errorMessage: error.message
            });
        }
    };

    const handleChildClick = (e) => {
        try {
            e.stopPropagation();
        } catch (error) {
            logError('Child click handler error', {
                operation: 'stopPropagation'
            }, error);
        }
    };

    const handleManageFirewall = async () => {
        const startTime = performance.now();

        try {
            setLoadingStates(prev => ({ ...prev, firewall: true }));

            logUserAction('manage_firewall_initiated', {
                currentRulesCount: securityRules?.length || 0,
                activeSection,
                isAdvancedRegion
            });

            logInfo('Opening firewall management modal', {
                currentRulesCount: securityRules?.length || 0
            });

            await new Promise(resolve => setTimeout(resolve, 100));

            setFirewallModalOpen(true);

            const endTime = performance.now();
            const duration = endTime - startTime;

            logPerformance('firewall_modal_open', duration, {
                success: true,
                currentRulesCount: securityRules?.length || 0
            });

            logUserAction('manage_firewall_success', {
                duration: `${duration.toFixed(2)}ms`
            });

        } catch (error) {
            const endTime = performance.now();
            const duration = endTime - startTime;

            handleOperationError(error, 'firewall');

            logPerformance('firewall_modal_open', duration, {
                success: false,
                error: error.message
            });

            logUserAction('manage_firewall_error', {
                errorMessage: error.message
            });
        } finally {
            setLoadingStates(prev => ({ ...prev, firewall: false }));
        }
    };

    const handleSaveFirewallRules = async (rules) => {
        const startTime = performance.now();

        try {
            setLoadingStates(prev => ({ ...prev, savingRules: true }));

            logUserAction('save_firewall_rules_initiated', {
                rulesCount: rules?.length || 0,
                previousRulesCount: securityRules?.length || 0
            });

            logInfo('Starting firewall rules save operation', {
                newRulesCount: rules?.length || 0,
                previousRulesCount: securityRules?.length || 0
            });

            if (!Array.isArray(rules)) {
                throw new Error('Invalid firewall rules format');
            }

            await new Promise(resolve => setTimeout(resolve, 150));

            onUpdateSecurityRules(rules);
            setFirewallModalOpen(false);

            const endTime = performance.now();
            const duration = endTime - startTime;

            logPerformance('save_firewall_rules', duration, {
                success: true,
                rulesCount: rules.length,
                previousRulesCount: securityRules?.length || 0
            });

            logInfo('Firewall rules saved successfully', {
                rulesCount: rules.length,
                duration: `${duration.toFixed(2)}ms`
            });

            logUserAction('save_firewall_rules_success', {
                rulesCount: rules.length,
                duration: `${duration.toFixed(2)}ms`
            });

        } catch (error) {
            const endTime = performance.now();
            const duration = endTime - startTime;

            handleOperationError(error, 'savingRules');

            logPerformance('save_firewall_rules', duration, {
                success: false,
                error: error.message
            });

            logUserAction('save_firewall_rules_error', {
                errorMessage: error.message,
                rulesCount: rules?.length || 0
            });
        } finally {
            setLoadingStates(prev => ({ ...prev, savingRules: false }));
        }
    };

    const handleTogglePublicIP = () => {
        try {
            const newState = !publicIP;

            logUserAction('toggle_public_ip', {
                from: publicIP,
                to: newState,
            });

            logInfo('Public IP setting toggled', {
                from: publicIP,
                to: newState,
            });

            onTogglePublicIP();

            logUserAction('toggle_public_ip_success', {
                newState,
            });
        } catch (error) {
            handleOperationError(error, 'validation');

            logUserAction('toggle_public_ip_error', {
                errorMessage: error.message
            });
        }
    };

    const handleAddCloudInit = () => {
        try {
            logUserAction('add_cloud_init_initiated', {
                activeSection
            });

            logInfo('Cloud-init script addition initiated');

            onAddCloudInit();

            logUserAction('add_cloud_init_success');
        } catch (error) {
            handleOperationError(error, 'validation');

            logUserAction('add_cloud_init_error', {
                errorMessage: error.message
            });
        }
    };

    const handleCreateScript = () => {
        try {
            logUserAction('create_script_initiated', {
                currentScriptsCount: scripts?.length || 0
            });

            logInfo('Script creation initiated');

            onCreateScript();

            logUserAction('create_script_modal_opened');
        } catch (error) {
            handleOperationError(error, 'scripts');

            logUserAction('create_script_error', {
                errorMessage: error.message
            });
        }
    };

    const handleEditScript = (scriptId) => {
        try {
            const script = scripts.find(s => s.id === scriptId);

            logUserAction('edit_script_initiated', {
                scriptId,
                scriptName: script?.name || 'Unknown'
            });

            logInfo('Script editing initiated', {
                scriptId,
                scriptName: script?.name
            });

            onEditScript(scriptId);

            logUserAction('edit_script_modal_opened', { scriptId });
        } catch (error) {
            handleOperationError(error, 'scripts');

            logUserAction('edit_script_error', {
                errorMessage: error.message,
                scriptId
            });
        }
    };

    const handleDeleteScript = (scriptId) => {
        try {
            const script = scripts.find(s => s.id === scriptId);

            logUserAction('delete_script_initiated', {
                scriptId,
                scriptName: script?.name || 'Unknown'
            });

            logInfo('Script deletion initiated', {
                scriptId,
                scriptName: script?.name
            });

            onDeleteScript(scriptId);

            logUserAction('delete_script_modal_opened', { scriptId });
        } catch (error) {
            handleOperationError(error, 'scripts');

            logUserAction('delete_script_error', {
                errorMessage: error.message,
                scriptId
            });
        }
    };

    const handleScriptSelection = (scriptId) => {
        try {
            const script = scripts.find(s => s.id === scriptId);

            logUserAction('script_selected', {
                scriptId,
                scriptName: script?.name || 'Unknown'
            });

            logInfo('Script selection changed', {
                scriptId,
                scriptName: script?.name
            });

            onScriptChange(scriptId);
            setIsScriptDropdownOpen(false);

            logUserAction('script_selection_success', { scriptId });
        } catch (error) {
            handleOperationError(error, 'scripts');

            logUserAction('script_selection_error', {
                errorMessage: error.message,
                scriptId
            });
        }
    };

    const handleRefreshScripts = useCallback(async () => {
        const startTime = performance.now();

        try {
            setLoadingStates(prev => ({ ...prev, scripts: true }));

            logUserAction('refresh_scripts_initiated', {
                currentScriptsCount: scripts?.length || 0
            });

            logInfo('Scripts refresh initiated');

            await onRefreshScripts();

            const endTime = performance.now();
            const duration = endTime - startTime;

            logPerformance('refresh_scripts_ui', duration, {
                success: true
            });

            logUserAction('refresh_scripts_success', {
                duration: `${duration.toFixed(2)}ms`
            });

        } catch (error) {
            const endTime = performance.now();
            const duration = endTime - startTime;

            handleOperationError(error, 'scripts');

            logPerformance('refresh_scripts_ui', duration, {
                success: false,
                error: error.message
            });

            logUserAction('refresh_scripts_error', {
                errorMessage: error.message
            });
        } finally {
            setLoadingStates(prev => ({ ...prev, scripts: false }));
        }
    }, [onRefreshScripts]);

    const handleOpenBulkDelete = () => {
        try {
            logUserAction('bulk_delete_modal_opened', {
                scriptsCount: scripts?.length || 0
            });

            logInfo('Bulk delete modal opened', {
                scriptsCount: scripts?.length || 0
            });

            setBulkDeleteModalOpen(true);
        } catch (error) {
            handleOperationError(error, 'scripts');

            logUserAction('bulk_delete_modal_error', {
                errorMessage: error.message
            });
        }
    };

    const handleBulkDelete = async (scriptIds) => {
        const startTime = performance.now();

        try {
            setLoadingStates(prev => ({ ...prev, bulkDeleting: true }));

            logUserAction('bulk_delete_initiated', {
                scriptsCount: scriptIds?.length || 0
            });

            logInfo('Bulk delete initiated', {
                scriptsCount: scriptIds?.length || 0
            });

            await onBulkDeleteScripts(scriptIds);

            const endTime = performance.now();
            const duration = endTime - startTime;

            logPerformance('bulk_delete_scripts', duration, {
                success: true,
                scriptsCount: scriptIds?.length || 0
            });

            logInfo('Bulk delete completed successfully', {
                scriptsCount: scriptIds?.length || 0,
                duration: `${duration.toFixed(2)}ms`
            });

            logUserAction('bulk_delete_success', {
                scriptsCount: scriptIds?.length || 0,
                duration: `${duration.toFixed(2)}ms`
            });

        } catch (error) {
            const endTime = performance.now();
            const duration = endTime - startTime;

            handleOperationError(error, 'scripts');

            logPerformance('bulk_delete_scripts', duration, {
                success: false,
                error: error.message
            });

            logUserAction('bulk_delete_error', {
                errorMessage: error.message,
                scriptsCount: scriptIds?.length || 0
            });

            throw error;
        } finally {
            setLoadingStates(prev => ({ ...prev, bulkDeleting: false }));
        }
    };

    const handleSectionChange = (sectionId) => {
        try {
            const previousSection = activeSection;
            setActiveSection(sectionId);

            logUserAction('section_change', {
                from: previousSection,
                to: sectionId,
                isExpanded,
                isAdvancedRegion
            });

            logInfo('Configuration section changed', {
                from: previousSection,
                to: sectionId,
                isAdvancedRegion
            });
        } catch (error) {
            logError('Error changing section', {
                from: activeSection,
                to: sectionId,
                isAdvancedRegion
            }, error);
        }
    };

    const toggleScriptDropdown = (e) => {
        e.stopPropagation();
        setIsScriptDropdownOpen(!isScriptDropdownOpen);
    };

    const handleScriptDropdownClick = (e) => {
        e.stopPropagation();
    };

    const LoadingIndicator = ({ text = "Loading..." }) => (
        <div className="flex items-center justify-center py-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#e0eacc] mr-2"></div>
            <span className="text-gray-300 text-xs">{text}</span>
        </div>
    );

    const sidebarItems = isAdvancedRegion 
        ? [{ id: 'startup', label: 'Startup' }]
        : [
            { id: 'network', label: 'Network' },
            { id: 'security', label: 'Security' },
            { id: 'startup', label: 'Startup' }
          ];

    const selectedScriptName = scripts.find(s => s.id === selectedScript)?.name || 'Select a script...';

    return (
        <>
            <style>
                {`
                    .custom-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: #555555 #2a2a2a;
                    }
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 6px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: #2a2a2a;
                        border-radius: 3px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #555555;
                        border-radius: 3px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #666666;
                    }
                `}
            </style>
            <div className="bg-[#121212] p-3 md:p-2 rounded-lg mb-4 md:mb-2 relative w-full md:w-[94%] md:mx-auto">
                <div
                    className="flex justify-between items-center cursor-pointer py-2 md:py-0 min-h-[48px] md:min-h-auto"
                    onClick={toggleExpanded}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            toggleExpanded();
                        }
                    }}
                >
                    <h3 className="font-semibold text-white text-sm">
                        {isAdvancedRegion ? 'Script Configuration' : 'Advanced Configuration'}
                    </h3>
                    <span className="text-white text-lg md:text-base">{isExpanded ? '▲' : '▼'}</span>
                </div>

                {isExpanded && (
                    <div
                        className="absolute left-0 right-0 bg-[#121212] p-3 md:p-2 rounded-lg mt-2 z-10 shadow-lg border border-[#333333]"
                        onClick={handleChildClick}
                    >
                        {!isAuthenticated ? (
                            <div className="flex flex-col items-center justify-center min-h-[300px] p-6">
                                <p className="text-gray-400 text-sm mb-4 text-center">
                                    Sign up to access network settings, security rules, and startup scripts.
                                </p>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.location.href = '/signup';
                                    }}
                                    className="bg-[#e0eacc] text-black px-5 py-1.5 rounded-md text-sm font-medium hover:bg-[#c8d9a9] transition-colors"
                                >
                                    Sign up to configure →
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col md:flex-row min-h-[300px]">
                                <div className="w-full md:w-1/4 md:border-r md:border-[#333333] md:pr-2 mb-4 md:mb-0">
                                    <div className="md:hidden flex gap-2 overflow-x-auto pb-2">
                                        {sidebarItems.map(item => (
                                            <button
                                                key={item.id}
                                                className={`
                                                    px-4 py-3 rounded-md whitespace-nowrap min-h-[48px] text-sm font-medium
                                                    ${activeSection === item.id
                                                        ? 'bg-[#e0eacc] text-black'
                                                        : 'bg-[#1a1a1a] text-gray-300 border border-[#333333]'}
                                                `}
                                                onClick={() => handleSectionChange(item.id)}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>

                                    <ul className="hidden md:block">
                                        {sidebarItems.map(item => (
                                            <li
                                                key={item.id}
                                                className={`
                                                    mb-2 px-3 py-[0.48rem] rounded-md cursor-pointer
                                                    ${activeSection === item.id
                                                        ? 'bg-[#e0eacc] text-black'
                                                        : 'text-gray-300 hover:bg-[#1a1a1a] hover:bg-opacity-50'}
                                                `}
                                                onClick={() => handleSectionChange(item.id)}
                                            >
                                                {item.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="w-full md:w-3/4 md:pl-4">
                                    {!isAdvancedRegion && activeSection === 'network' && (
                                        <div>
                                            <h4 className="text-white font-medium mb-4 md:mb-3 text-sm">Network Configuration</h4>
                                            <div className="mb-4 md:mb-3 pt-1">
                                                <div
                                                    className="flex justify-between items-center cursor-pointer py-2 md:py-0 min-h-[52px] md:min-h-auto"
                                                    onClick={handleTogglePublicIP}
                                                >
                                                    <span className="text-white text-sm">Public IP</span>
                                                    <div
                                                        className={`w-14 h-7 md:w-12 md:h-6 flex items-center rounded-full p-1 ${
                                                            publicIP ? 'bg-[#e0eacc]' : 'bg-[#333333]'
                                                        }`}
                                                        role="checkbox"
                                                        aria-checked={publicIP}
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                handleTogglePublicIP();
                                                            }
                                                        }}
                                                    >
                                                        <div
                                                            className={`w-5 h-5 md:w-4 md:h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                                                                publicIP ? 'bg-white translate-x-7 md:translate-x-6' : 'bg-gray-400'
                                                            }`}
                                                        ></div>
                                                    </div>
                                                </div>
                                                {publicIP && (
                                                    <div className="mt-3 md:mt-2 -mb-1 py-2 pl-0 text-xs text-yellow-400">
                                                        <span role="alert">
                                                            Warning: Using a public IP makes your VM accessible from the internet.
                                                            <br />
                                                            Make sure to secure your VM properly.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {!isAdvancedRegion && activeSection === 'security' && (
                                        <div>
                                            <div className="mb-4 md:mb-3">
                                                <p className="text-white mb-4 text-sm">Firewall</p>

                                                {loadingStates.firewall && (
                                                    <LoadingIndicator text="Loading firewall settings..." />
                                                )}

                                                <button
                                                    className={`w-full md:w-auto px-[19px] md:px-[26.6px] bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white py-[9.5px] md:py-[3.8px] rounded transition-all min-h-[39px] md:min-h-auto ${
                                                        loadingStates.firewall ? 'opacity-50 cursor-not-allowed' : ''
                                                    }`}
                                                    style={{ fontSize: '13.3px' }}
                                                    onClick={handleManageFirewall}
                                                    type="button"
                                                    disabled={loadingStates.firewall}
                                                >
                                                    {loadingStates.firewall ? 'LOADING...' : 'MANAGE FIREWALL'}
                                                </button>

                                                {securityRules && securityRules.length > 0 && (
                                                    <div className="mt-3 text-xs text-[#e0eacc]">
                                                        {securityRules.length} firewall rule{securityRules.length > 1 ? 's' : ''} configured
                                                    </div>
                                                )}

                                                {loadingStates.savingRules && (
                                                    <LoadingIndicator text="Saving firewall rules..." />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {activeSection === 'startup' && (
                                        <div>
                                            {isAdvancedRegion ? (
                                                <div>
                                                    <div className="mb-4">
                                                        <label className="block text-white text-sm font-medium mb-2" style={{ transform: 'translateX(-2px)' }}>
                                                            Select Script
                                                        </label>
                                                        {loadingScripts || loadingStates.scripts ? (
                                                            <LoadingIndicator text="Loading scripts..." />
                                                        ) : (
                                                            <div className="relative w-[67%]" ref={scriptDropdownRef}>
                                                                <div 
                                                                    className={`w-full p-[13.2px] md:p-[8.8px] bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] rounded text-white text-sm flex justify-between items-center cursor-pointer ${
                                                                        scripts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    onClick={scripts.length > 0 ? toggleScriptDropdown : undefined}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    aria-expanded={isScriptDropdownOpen}
                                                                    aria-haspopup="listbox"
                                                                    onKeyDown={(e) => {
                                                                        if (scripts.length > 0 && (e.key === 'Enter' || e.key === ' ')) {
                                                                            e.preventDefault();
                                                                            toggleScriptDropdown(e);
                                                                        }
                                                                    }}
                                                                >
                                                                    <span>{scripts.length === 0 ? 'No scripts available' : selectedScriptName}</span>
                                                                    <div className="flex items-center gap-1">
                                                                        {Array.from({ length: 3 }).map((_, i) => (
                                                                            <div 
                                                                                key={i}
                                                                                className="w-1 h-1 bg-[#cad9e3] rounded-full animate-bounce"
                                                                                style={{ 
                                                                                    animationDelay: `${i * 150}ms`,
                                                                                    animationDuration: '1s',
                                                                                    animationIterationCount: 'infinite'
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                
                                                                {isScriptDropdownOpen && scripts.length > 0 && (
                                                                    <div 
                                                                        className="absolute z-10 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded shadow-lg max-h-32 overflow-auto custom-scrollbar"
                                                                        onClick={handleScriptDropdownClick}
                                                                        role="listbox"
                                                                    >
                                                                        <div className="py-1">
                                                                            {scripts.map((script) => (
                                                                                <div 
                                                                                    key={script.id} 
                                                                                    className={`px-3 md:px-2 py-[13.2px] md:py-[8.8px] cursor-pointer flex items-center justify-between ${
                                                                                        script.id === selectedScript 
                                                                                            ? 'bg-[#252525] text-[#e0eacc]' 
                                                                                            : 'text-gray-200 hover:bg-[#252525] hover:text-[#e0eacc]'
                                                                                    }`}
                                                                                    onClick={() => handleScriptSelection(script.id)}
                                                                                    role="option"
                                                                                    aria-selected={script.id === selectedScript}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                                            e.preventDefault();
                                                                                            handleScriptSelection(script.id);
                                                                                        }
                                                                                    }}
                                                                                    tabIndex={0}
                                                                                    title={script.script_preview ? `Preview: ${script.script_preview.substring(0, 200)}` : script.name}
                                                                                >
                                                                                    <span className="truncate">{sanitizeText(script.name)}</span>
                                                                                    {script.updated_at && (
                                                                                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                                                                            {new Date(script.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {scripts.length === 0 && !loadingScripts && !loadingStates.scripts && (
                                                            <p className="mt-1 text-xs text-gray-400">
                                                                No scripts found. Create a new script to get started.
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                                        <button
                                                            className="w-full md:w-auto px-4 py-2 bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded font-medium transition-all min-h-[41px] md:min-h-auto"
                                                            style={{ fontSize: '11.7px' }}
                                                            onClick={handleCreateScript}
                                                            type="button"
                                                        >
                                                            + CREATE
                                                        </button>
                                                        
                                                        <button
                                                            className={`w-full md:w-auto px-4 py-2 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white rounded transition-all min-h-[41px] md:min-h-auto ${
                                                                !selectedScript ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            style={{ fontSize: '11.7px' }}
                                                            onClick={() => selectedScript && handleEditScript(selectedScript)}
                                                            type="button"
                                                            disabled={!selectedScript}
                                                        >
                                                            EDIT
                                                        </button>
                                                        
                                                        <button
                                                            className={`w-full md:w-auto bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)] font-medium py-2 px-4 rounded transition-all min-h-[41px] md:min-h-auto ${
                                                                !selectedScript || loadingStates.deletingScript ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            style={{ fontSize: '11.7px' }}
                                                            onClick={() => selectedScript && handleDeleteScript(selectedScript)}
                                                            type="button"
                                                            disabled={!selectedScript || loadingStates.deletingScript}
                                                        >
                                                            <div className="flex items-center justify-center w-full">
                                                                <FaTrash size={12} className="mr-2" />
                                                                <span>{loadingStates.deletingScript === selectedScript ? 'DELETING...' : 'DELETE'}</span>
                                                            </div>
                                                        </button>
                                                        
                                                        <button
                                                            className={`w-full md:w-auto px-4 py-2 bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)] rounded font-medium transition-all min-h-[41px] md:min-h-auto ${
                                                                scripts.length === 0 || loadingStates.bulkDeleting ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            style={{ fontSize: '11.7px' }}
                                                            onClick={handleOpenBulkDelete}
                                                            type="button"
                                                            disabled={scripts.length === 0 || loadingStates.bulkDeleting}
                                                        >
                                                            {loadingStates.bulkDeleting ? 'LOADING...' : 'BULK DELETE'}
                                                        </button>
                                                    </div>

                                                    {selectedScript && (
                                                        <div className="bg-[#1a1a1a] border border-[#333333] rounded-md p-3">
                                                            <h5 className="text-white font-medium text-sm mb-2">Selected Script</h5>
                                                            {(() => {
                                                                const script = scripts.find(s => s.id === selectedScript);
                                                                return script ? (
                                                                    <div>
                                                                        <p className="text-gray-300 text-sm">
                                                                            <span className="font-medium">Name:</span> {sanitizeText(script.name)}
                                                                        </p>
                                                                        {script.script_preview && (
                                                                            <div className="mt-1">
                                                                                <p className="text-gray-400 text-xs">
                                                                                    <span className="font-medium">Preview:</span>
                                                                                </p>
                                                                                <div 
                                                                                    className="text-gray-400 text-xs mt-1 font-mono bg-[#0a0a0a] border border-[#333333] rounded p-2 max-h-20 overflow-y-auto"
                                                                                    title={script.script_preview.length > 100 ? "Content truncated. Open editor to view full script." : ""}
                                                                                >
                                                                                    {sanitizeText(script.script_preview)}
                                                                                    {script.script_preview.length > 100 && (
                                                                                        <span className="text-yellow-400 ml-1">[truncated]</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {script.updated_at && (
                                                                            <p className="text-gray-400 text-xs mt-1">
                                                                                <span className="font-medium">Last updated:</span> {new Date(script.updated_at).toLocaleDateString()}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-gray-400 text-sm">Script information not available</p>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}

                                                    <div className="mt-3 text-xs text-gray-400">
                                                        Startup scripts run automatically during VM initialization with root privileges.
                                                        Scripts are optional for VM deployment.
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <h4 className="text-white font-medium mb-4 md:mb-3 text-sm">
                                                        Startup
                                                    </h4>
                                                    <div className="my-4 md:my-3">
                                                        <button
                                                            className="w-full md:w-auto px-[19px] md:px-[26.6px] bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] hover:text-[#e0eacc] text-white py-[9.5px] md:py-[3.8px] rounded transition-all min-h-[39px] md:min-h-auto"
                                                            style={{ fontSize: '13.3px' }}
                                                            onClick={handleAddCloudInit}
                                                            type="button"
                                                        >
                                                            ADD CLOUD-INIT SCRIPT
                                                        </button>
                                                        <div className="mt-3 text-xs text-gray-400">
                                                            Cloud-init scripts run with root privileges during VM initialization
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <FirewallModal
                    isOpen={firewallModalOpen}
                    onClose={() => {
                        setFirewallModalOpen(false);

                        logUserAction('close_firewall_modal', {
                            hadRules: securityRules && securityRules.length > 0
                        });

                        logInfo('Firewall modal closed', {
                            hadRules: securityRules && securityRules.length > 0
                        });
                    }}
                    onSave={handleSaveFirewallRules}
                    initialRules={securityRules || []}
                />

                <BulkDeleteModal
                    isOpen={bulkDeleteModalOpen}
                    onClose={() => {
                        setBulkDeleteModalOpen(false);

                        logUserAction('close_bulk_delete_modal', {
                            scriptsCount: scripts?.length || 0
                        });

                        logInfo('Bulk delete modal closed', {
                            scriptsCount: scripts?.length || 0
                        });
                    }}
                    scripts={scripts}
                    onDelete={handleBulkDelete}
                    onRefresh={handleRefreshScripts}
                />
            </div>
        </>
    );
};

AdvancedConfiguration.propTypes = {
    publicIP: PropTypes.bool,
    onTogglePublicIP: PropTypes.func,
    onManageFirewall: PropTypes.func,
    onAddCloudInit: PropTypes.func,
    securityRules: PropTypes.array,
    onUpdateSecurityRules: PropTypes.func,
    isAdvancedRegion: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
    scripts: PropTypes.array,
    selectedScript: PropTypes.string,
    onScriptChange: PropTypes.func,
    onCreateScript: PropTypes.func,
    onEditScript: PropTypes.func,
    onDeleteScript: PropTypes.func,
    onRefreshScripts: PropTypes.func,
    loadingScripts: PropTypes.bool,
    onBulkDeleteScripts: PropTypes.func
};

AdvancedConfiguration.defaultProps = {
    publicIP: true,
    onTogglePublicIP: () => {},
    onManageFirewall: () => {},
    onAddCloudInit: () => {},
    securityRules: [],
    onUpdateSecurityRules: () => {},
    isAdvancedRegion: false,
    isAuthenticated: false,
    scripts: [],
    selectedScript: '',
    onScriptChange: () => {},
    onCreateScript: () => {},
    onEditScript: () => {},
    onDeleteScript: () => {},
    onRefreshScripts: () => {},
    loadingScripts: false,
    onBulkDeleteScripts: () => {}
};

const AdvancedConfigurationComponent = React.memo(AdvancedConfiguration);
export default AdvancedConfigurationComponent;